import { useCallback } from 'react';
import { GraphQLErrors } from '@atlassian/jira-business-graphql-errors';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fetchJson } from '@atlassian/jira-fetch';
import { createAri } from '@atlassian/jira-platform-ari';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { QUERY } from './gql';
import {
	SUCCESS_RESPONSE_TYPE,
	type OverviewSuccess,
	type OverviewMetaQueryProject,
	type OverviewDetailsResponse,
} from './types';

const OPERATION_NAME = 'JWMOverviewMeta';

export const useFetchOverviewMeta = () => {
	const { cloudId, activationId } = useTenantContext();

	return useCallback(
		async (overviewId: string) => {
			try {
				const response = await fetchJson<OverviewDetailsResponse>(
					`/gateway/api/graphql?operation=${OPERATION_NAME}`,
					{
						method: 'POST',
						body: JSON.stringify({
							query: QUERY,
							variables: {
								id: createAri({
									resourceOwner: 'jira',
									cloudId,
									resourceType: 'project-overview',
									resourceId: overviewId,
									activationId,
								}),
							},
						}),
					},
				);

				if (response?.data?.jira?.overview?.__typename === SUCCESS_RESPONSE_TYPE) {
					const mappedProjectDetails = new Map<
						OverviewMetaQueryProject['projectId'],
						OverviewMetaQueryProject['lead']
					>();

					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					(response.data.jira.overview as OverviewSuccess).projects.edges.forEach(({ node }) => {
						if (node?.lead) {
							mappedProjectDetails.set(node.projectId, node.lead);
						}
					});

					return mappedProjectDetails;
				}

				throw new GraphQLErrors(response.errors);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error.statusCode !== 404) {
					fireErrorAnalytics({
						meta: {
							id: OPERATION_NAME,
							packageName: 'jiraBusinessSummary',
							teamName: 'wanjel',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				}

				throw error;
			}
		},
		[cloudId, activationId],
	);
};
